import {UserType} from '../../../../../backend/enums/user-type';
import {EntityInfo} from '../../../../../backend/interfaces/entity-info';
import {Occupation} from '../../../../../backend/interfaces/occupation';
import {Role} from '../../../../../backend/interfaces/role';
import {EntityReportsRepository} from '../../../../../repositories/entity-reports';
import {OccupationsRepository} from '../../../../../repositories/occupations';
import {RolesRepository} from '../../../../../repositories/roles';

export type ExportProfessionalCaregiversReportData = {
  users: any[];
  roles: Record<string, Role>;
  occupations: Record<string, Occupation>;
};

export async function exportProfessionalCaregiversGetReportData({
  entity,
}: {
  entity?: EntityInfo;
}): Promise<ExportProfessionalCaregiversReportData | null> {
  if (!entity?.id) {
    return null;
  }

  // 1. Get users base data
  const [users] = await Promise.all([
    EntityReportsRepository.instance.affliations({
      entityId: entity.id,
      body: {
        types: [UserType.Caregiver],
        users: {
          isActive: true,
          propertiesToInclude: [
            'birthday',
            'gender',
            'name',
            'displayName',
            'occupationId',
            'languageCode',
            'phoneNumber',
            'picture',
            'email',
            'address',
            'region',
            'taxPayerNumber',
            'isActive',
          ],
        },
        propertiesToInclude: ['rolesIds'],
      },
    }),
  ]);

  // 2. Get more data based on users data
  const [roles, occupations] = await Promise.all([
    getAllRoles(entity.id),
    getAllOccupations(users),
  ]);

  // 3. Done
  return {
    users,
    roles,
    occupations,
  };
}

// ------------------------------------------------------------------
// ------------------------------------------------------------------
// ------------------------------------------------------------------

async function getAllRoles(entityId: string): Promise<Record<string, Role>> {
  const results: Record<string, Role> = {};
  let skip = 0;
  const limit = 20;

  while (true) {
    const {data, totalCount} = await RolesRepository.instance.getRoles(entityId, limit, skip);
    for (const role of data) {
      if (role.id) {
        results[role.id] = role;
      }
    }

    skip += data.length;

    if (data.length === 0 || skip >= totalCount) {
      break;
    }
  }

  return results;
}

async function getAllOccupations(users: any[]): Promise<Record<string, Occupation>> {
  const proms: Promise<Occupation[]>[] = [];
  const result: Record<string, Occupation> = {};

  // Get occupation ids from users
  const occupationIds = Array.from(
    new Set(users.flatMap((u) => (u.userId.occupationId ? [u.userId.occupationId] : [])))
  );

  // Get occupation details for each occupation id
  for (const occupationId of occupationIds) {
    proms.push(OccupationsRepository.instance.getById(occupationId, true));
  }

  // Wait for all to be fetched
  const occupationDetails = await Promise.all(proms);

  // Map them by id
  for (const occupationDetail of occupationDetails) {
    const found = occupationDetail.find((o) => occupationIds.includes(o.id ?? ''));
    if (found?.id) {
      result[found.id] = found;
    }
  }

  // Done
  return result;
}
